import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  BankDepositIcon,
  Box,
  ClockIcon,
  ErrorOutlineIcon,
  FeeLabel,
  Preloader,
  Text,
  useResponsive,
  WarningCard,
} from '@common-fe/common-fe';

import PaymentTypeIcon from '@/components/elements/PaymentTypeIcon';
import { PaymentMethodTiming } from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/ChoosePaymentMethod/paymentMethodLayout.types';
import { PaymentMethod } from '@/modules/transaction/components/ReimburseMe/types/payment.types';

import { getPaymentMethodTitle } from './getPaymentMethodTitle';

interface DefaultPaymentMethodBlockProps {
  isLoading: boolean;
  activeMethod: PaymentMethod | undefined;
  paymentEmployeeFee: string | undefined;
  isVerificationFailed: boolean;
  isVerificationPending: boolean;
  onAddOrChangeClick: () => void;
}

const DefaultPaymentMethodBlock: React.FC<DefaultPaymentMethodBlockProps> = ({
  isLoading,
  activeMethod,
  paymentEmployeeFee,
  isVerificationFailed,
  isVerificationPending,
  onAddOrChangeClick,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  return (
    <Box gap="spacing8">
      <Text weight={700} color="textTitle" size="medium">
        {t('How should we reimburse you?')}
      </Text>

      {isLoading ? (
        <Box
          background="border2"
          round="container2Round"
          style={{ overflow: 'hidden' }}
          pad="spacing16"
          align="center"
          justify="center"
          height={{ min: '72px' }}
        >
          <Preloader />
        </Box>
      ) : (
        <Box gap="spacing8">
          <Box
            background="border2"
            round="container2Round"
            style={{ overflow: 'hidden' }}
            direction={isMobile ? 'column' : 'row'}
            align={isMobile ? 'stretch' : 'center'}
            pad="spacing16"
            gap="spacing12"
          >
            <Box flex gap="spacing8">
              <Box direction="row" align="center" gap="spacing8">
                <Box width="24px" height="24px" flex={{ shrink: 0 }} align="center" justify="center">
                  {activeMethod?.type ? (
                    <PaymentTypeIcon paymentType={activeMethod.type} size="24px" />
                  ) : (
                    <BankDepositIcon size="24px" color="iconAccent" />
                  )}
                </Box>

                <Text
                  size="20px"
                  weight={700}
                  color="textTitle"
                  style={{
                    lineHeight: '28px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {activeMethod ? getPaymentMethodTitle(activeMethod) : t('No Payment Method Added')}
                </Text>
              </Box>

              {isVerificationFailed && (
                <Text size="small" color="danger" weight={400} style={{ lineHeight: '20px' }}>
                  {t('Account verification failed')}
                </Text>
              )}
              {isVerificationPending && (
                <Text size="small" color="missingDetail" weight={400} style={{ lineHeight: '20px' }}>
                  {t('Account verification pending')}
                </Text>
              )}

              {activeMethod?.type && (
                <Box direction="row" align="center" gap="spacing4" wrap style={{ rowGap: '4px' }}>
                  <FeeLabel amount={paymentEmployeeFee} testId={activeMethod.type} size="medium" withBorder />

                  <Box
                    direction="row"
                    align="center"
                    gap="spacing4"
                    background="canvas"
                    round="xsmall"
                    pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
                  >
                    <ClockIcon size="16px" color="iconSecondary" style={{ flexShrink: 0 }} />
                    <Text size="medium" color="textBody" weight={400} style={{ whiteSpace: 'nowrap' }}>
                      {PaymentMethodTiming?.[activeMethod?.type]}
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>

            <AppButton
              buttonType="secondary"
              testId="default-payment-method_action-btn"
              onClick={onAddOrChangeClick}
              width={isMobile ? '100%' : '100px'}
              size="M"
            >
              {activeMethod ? t('Change') : t('Add')}
            </AppButton>
          </Box>

          {isVerificationFailed && (
            <WarningCard
              testId="default-payment-method_verification-failed"
              backgroundColor="dangerContainer"
              borderColor="dangerBorder"
              topBorderColor="dangerContainer"
              containerProps={{ pad: 'spacing16', gap: 'spacing12' }}
              icon={<ErrorOutlineIcon color="danger" size="24px" style={{ flexShrink: 0 }} />}
              title={
                <Box gap="spacing2">
                  <Text weight={700} color="textBody" size="medium" style={{ lineHeight: '20px' }}>
                    {t('Your bank account could not be verified')}
                  </Text>
                  <Text color="textBody" size="medium" style={{ lineHeight: '20px' }}>
                    {t('Update banking account information to try again or add a new bank account.')}
                  </Text>
                </Box>
              }
            >
              <Box direction="row">
                <AppButton
                  testId="default-payment-method_try-again-btn"
                  onClick={onAddOrChangeClick}
                  size="M"
                >
                  {t('Try Again')}
                </AppButton>
              </Box>
            </WarningCard>
          )}

          {isVerificationPending && (
            <WarningCard
              testId="default-payment-method_verification-pending"
              backgroundColor="missingContainer"
              borderColor="missingBorder"
              topBorderColor="missingContainer"
              containerProps={{ pad: 'spacing16' }}
              title={
                <Box gap="spacing2">
                  <Text weight={700} color="textBody" size="medium" style={{ lineHeight: '20px' }}>
                    {t('Your account verification is in progress')}
                  </Text>
                  <Text weight={400} color="textBody" size="medium" style={{ lineHeight: '20px' }}>
                    {t(
                      'A small deposit will be posted to your account within one to two business days. Once posted, you will need to provide additional details to verify your account.'
                    )}
                  </Text>
                </Box>
              }
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default DefaultPaymentMethodBlock;
